import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload, useBlocker } from "react-router-dom";

interface UsePollingProps<T, K> {
  pollInterval: number;
  timeoutDuration: number;
  callback(pollInterval: number): { data: T; error?: K };
  onSuccess(): void;
  onPolling(): void;
  onError(): void;
  blockNavigationWhenPolling?: boolean;
}

export function usePolling<T extends object, K extends object>({
  pollInterval,
  timeoutDuration,
  callback,
  onSuccess,
  onPolling,
  onError,
  blockNavigationWhenPolling = false,
}: UsePollingProps<T, K>) {
  const [isPolling, setIsPolling] = useState(true);
  const [isBlockingNavigation, setIsBlockingNavigation] = useState(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, error } = callback(isPolling ? pollInterval : 0);

  useBlocker(
    ({ currentLocation, nextLocation }) =>
      isBlockingNavigation &&
      blockNavigationWhenPolling &&
      currentLocation.pathname !== nextLocation.pathname
  );

  const beforeUnloadHandler = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isBlockingNavigation && blockNavigationWhenPolling) {
        event.preventDefault();
      }

      return isBlockingNavigation && blockNavigationWhenPolling;
    },
    [isBlockingNavigation, blockNavigationWhenPolling]
  );

  useBeforeUnload(beforeUnloadHandler);

  useEffect(() => {
    if (isPolling) {
      onPolling();
    }
  }, [isPolling]);

  useEffect(() => {
    timer.current = setTimeout(() => {
      setIsBlockingNavigation(false);
      setTimeout(() => {
        onError();
      }, 300);
    }, timeoutDuration);

    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }

    const isDataLoaded = Object.values(data).every(
      (property) => property !== null
    );

    if (isDataLoaded) {
      setIsPolling(false);
      setIsBlockingNavigation(false);
      timer.current && clearTimeout(timer.current);
      setTimeout(() => {
        onSuccess();
      }, 300);
    }
  }, [data]);

  useEffect(() => {
    if (error && isPolling) {
      setIsBlockingNavigation(false);
      timer.current && clearTimeout(timer.current);
      setTimeout(() => {
        onError();
      }, 300);
    }
  }, [error]);

  return { data, isPolling };
}
